import type * as React from 'react'
import type { UseQueryResult } from '@tanstack/react-query'
import { EmptyState } from '@/components/ui/empty-state'
import { parseMoneyCents } from '@/lib/money'
import {
  SortableTableHead,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

export interface AgingRow {
  contact_id: number | null
  contact_name: string
  current: string
  days_1_to_30: string
  days_31_to_60: string
  days_61_to_90: string
  over_90: string
  account_credit: string
  total: string
}

export interface AgingReport {
  as_of: string
  rows: AgingRow[]
  total: AgingRow
}

export interface AgingReportResponse {
  aging_report: AgingReport
}

export interface AgingContactDrillDown {
  contactId: number
  contactName: string
}

export type AgingSortKey =
  | 'contact'
  | 'current'
  | 'days_1_to_30'
  | 'days_31_to_60'
  | 'days_61_to_90'
  | 'over_90'
  | 'total'

export interface AgingSortState {
  key: AgingSortKey
  direction: 'ascending' | 'descending'
}

const columns: Array<{
  key: keyof AgingRow
  sortKey?: AgingSortKey
  label: string
  overdue?: boolean
  drillable?: boolean
}> = [
  { key: 'account_credit', label: 'Credit' },
  { key: 'current', sortKey: 'current', label: 'Current', drillable: true },
  { key: 'days_1_to_30', sortKey: 'days_1_to_30', label: '1–30', drillable: true },
  { key: 'days_31_to_60', sortKey: 'days_31_to_60', label: '31–60', drillable: true },
  { key: 'days_61_to_90', sortKey: 'days_61_to_90', label: '61–90', drillable: true },
  { key: 'over_90', sortKey: 'over_90', label: '90+', overdue: true, drillable: true },
  { key: 'total', sortKey: 'total', label: 'Total', drillable: true },
]

export function AgingReportBody({
  query,
  isFiltered,
  sort,
  onSort,
  onDrillDown,
}: {
  query: UseQueryResult<AgingReportResponse, Error>
  isFiltered: boolean
  sort: AgingSortState | null
  onSort: (key: AgingSortKey) => void
  onDrillDown: (drillDown: AgingContactDrillDown) => void
}) {
  if (query.isLoading) return <EmptyState title="Loading A/R aging" className="min-h-52" />
  if (query.isError) return <EmptyState title="Unable to load A/R aging" className="min-h-52" />

  const report = query.data?.aging_report
  if (!report || report.rows.length === 0) {
    if (isFiltered) {
      return (
        <EmptyState
          className="min-h-52"
          variant="quiet"
          title="No matching balances."
          description="Try changing the search or level filters."
        />
      )
    }

    return (
      <EmptyState
        className="min-h-52"
        variant="quiet"
        title="Nothing outstanding."
        description="Every invoice is paid or current."
      />
    )
  }

  return <AgingTable report={report} sort={sort} onSort={onSort} onDrillDown={onDrillDown} />
}

function AgingTable({
  report,
  sort,
  onSort,
  onDrillDown,
}: {
  report: AgingReport
  sort: AgingSortState | null
  onSort: (key: AgingSortKey) => void
  onDrillDown: (drillDown: AgingContactDrillDown) => void
}) {
  return (
    <div className="overflow-hidden rounded-md border border-border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <AgingSortHead sortKey="contact" sort={sort} onSort={onSort}>
              Contact
            </AgingSortHead>
            {columns.map((column) =>
              column.sortKey ? (
                <AgingSortHead
                  key={column.key}
                  sortKey={column.sortKey}
                  sort={sort}
                  onSort={onSort}
                  className="text-right"
                >
                  {column.label}
                </AgingSortHead>
              ) : (
                <TableHead key={column.key} className="text-right">
                  {column.label}
                </TableHead>
              ),
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {report.rows.map((row) => (
            <AgingTableRow
              key={row.contact_id ?? row.contact_name}
              row={row}
              onDrillDown={onDrillDown}
            />
          ))}
          <AgingTableRow row={report.total} total onDrillDown={onDrillDown} />
        </TableBody>
      </Table>
    </div>
  )
}

function AgingTableRow({
  row,
  total = false,
  onDrillDown,
}: {
  row: AgingRow
  total?: boolean
  onDrillDown: (drillDown: AgingContactDrillDown) => void
}) {
  return (
    <TableRow className={total ? 'border-t-2 font-semibold hover:bg-transparent' : undefined}>
      <TableCell className="font-medium">{row.contact_name}</TableCell>
      {columns.map((column) => (
        <MoneyCell
          key={column.key}
          amount={String(row[column.key])}
          overdue={column.overdue}
          drillLabel={column.label}
          row={total ? undefined : row}
          drillable={column.drillable}
          onDrillDown={onDrillDown}
        />
      ))}
    </TableRow>
  )
}

function MoneyCell({
  amount,
  overdue = false,
  drillLabel,
  row,
  drillable = false,
  onDrillDown,
}: {
  amount: string
  overdue?: boolean
  drillLabel: string
  row?: AgingRow
  drillable?: boolean
  onDrillDown: (drillDown: AgingContactDrillDown) => void
}) {
  const zero = parseMoneyCents(amount) === 0
  const tone = zero ? 'text-muted-foreground' : overdue ? 'text-destructive' : ''
  return (
    <TableCell className={`text-right tabular-nums ${tone}`}>
      {drillable && row?.contact_id ? (
        <button
          type="button"
          className="underline decoration-border underline-offset-4 hover:decoration-current"
          aria-label={`${row.contact_name} ${drillLabel} ${amount}`}
          onClick={() =>
            onDrillDown({ contactId: row.contact_id as number, contactName: row.contact_name })
          }
        >
          {amount}
        </button>
      ) : (
        amount
      )}
    </TableCell>
  )
}

function AgingSortHead({
  sortKey,
  sort,
  onSort,
  children,
  className,
}: {
  sortKey: AgingSortKey
  sort: AgingSortState | null
  onSort: (key: AgingSortKey) => void
  children: React.ReactNode
  className?: string
}) {
  return (
    <SortableTableHead
      className={className}
      active={sort?.key === sortKey}
      direction={sort?.key === sortKey ? sort.direction : 'ascending'}
      onToggle={() => onSort(sortKey)}
    >
      {children}
    </SortableTableHead>
  )
}

export function nextAgingSort(current: AgingSortState | null, key: AgingSortKey): AgingSortState {
  if (current?.key !== key) return { key, direction: 'ascending' }
  return { key, direction: current.direction === 'ascending' ? 'descending' : 'ascending' }
}
